import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Target,
  Brain,
  Flame,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  TrendingUp,
  Clock,
  Zap,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  BarChart3,
  Lightbulb,
  Sparkles,
  Lock,
  RotateCw,
  Globe2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useState, useEffect } from "react";

interface StrugglingObjective {
  code: string;
  description: string;
  accuracy: number;
}

interface StudyTopic {
  subject: string;
  subjectDisplay: string;
  topic: string;
  topicDisplay: string;
  mastery: number;
  accuracy: number | null;
  totalAttempted: number;
  lastStudiedAt: string | null;
  daysSinceStudied: number;
  currentStreak: number;
  priorityScore: number;
  recommendedQuestions: number;
  completedToday: boolean;
  todayAttempts: number;
  todayAccuracy: number | null;
  objectivesMastered: number;
  objectivesTotal: number;
  objectivesProgressing: number;
  strugglingObjectiveCount: number;
  strugglingObjectives: StrugglingObjective[];
  reasons: string[];
  // Data moat: learned prerequisites and the forgetting curve.
  unlocks: string[];
  blockedBy: string | null;
  reviewDue: boolean;
  retention: number | null;
  halfLifeDays: number | null;
}

interface AbilityEstimate {
  percentile: number;
  theta: number;
  se: number;
  responses: number;
  source: "irt" | "mastery-fallback" | "none";
}

interface ReviewDueTopic {
  subject: string;
  topic: string;
  topicKey: string;
  mastery: number;
  daysSince: number;
  halfLifeDays: number;
  retention: number;
}

interface SubjectBreakdown {
  subject: string;
  displayName: string;
  avgMastery: number;
  avgAccuracy: number | null;
  topicCount: number;
  weakTopicCount: number;
  objectivesTotal: number;
  objectivesMastered: number;
  objectiveMasteryRate: number;
}

interface FocusArea {
  code: string;
  description: string;
  topicName: string | null;
  subject: string;
  accuracy: number;
}

interface ObjectiveSummary {
  total: number;
  mastered: number;
  progressing: number;
  notStarted: number;
  masteryRate: number;
}

interface StudyPlanData {
  grade: number;
  dailyPlan: StudyTopic[];
  completedCount: number;
  totalPlanItems: number;
  subjectBreakdown: SubjectBreakdown[];
  focusSubject: {
    name: string;
    mastery: number;
    accuracy: number | null;
    weakTopics: number;
    objectivesMastered: number;
    objectivesTotal: number;
  } | null;
  objectiveSummary: ObjectiveSummary;
  topFocusAreas: FocusArea[];
  allTopics: StudyTopic[];
  totalTopics: number;
  generatedAt: string;
  reviewDue?: ReviewDueTopic[];
  ability?: AbilityEstimate | null;
}

function getMasteryColor(mastery: number): string {
  if (mastery >= 80) return "text-emerald-600 dark:text-emerald-400";
  if (mastery >= 60) return "text-blue-600 dark:text-blue-400";
  if (mastery >= 40) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function getMasteryLabel(mastery: number): string {
  if (mastery >= 80) return "Strong";
  if (mastery >= 60) return "Good";
  if (mastery >= 40) return "Developing";
  return "Needs Focus";
}

function getSubjectIcon(subject: string): string {
  const s = subject.toLowerCase();
  if (s.includes("math")) return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300";
  if (s.includes("english") || s.includes("language")) return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300";
  if (s.includes("science")) return "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300";
  if (s.includes("social")) return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300";
  return "bg-muted text-muted-foreground";
}

// At-a-glance version of the plan's `reasons` sentences.
function PlanChip({
  icon: Icon,
  className,
  children,
  testId,
}: {
  icon: typeof Sparkles;
  className: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-tight ${className}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate max-w-[11rem]">{children}</span>
    </span>
  );
}

function TopicCard({ topic, index }: { topic: StudyTopic; index: number }) {
  const [, setLocation] = useLocation();

  const handleStartStudy = () => {
    setLocation(`/learning?subject=${encodeURIComponent(topic.subject)}&topic=${encodeURIComponent(topic.topic)}`);
  };

  return (
    <Card className={`relative ${topic.completedToday ? 'opacity-70' : ''}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-sm font-bold ${
            topic.completedToday ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-primary/10 text-primary'
          }`}>
            {topic.completedToday ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-medium text-sm leading-tight">{topic.topicDisplay}</h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <Badge variant="secondary" className={`${getSubjectIcon(topic.subject)}`}>
                    {topic.subjectDisplay}
                  </Badge>
                  {topic.currentStreak > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                      <Flame className="h-3 w-3" />{topic.currentStreak}
                    </span>
                  )}
                </div>
              </div>
              {!topic.completedToday && (
                <Button size="sm" onClick={handleStartStudy} className="flex-shrink-0">
                  Study <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
            <div className="mt-2 space-y-1.5">
              {(topic.reviewDue || topic.blockedBy || (topic.unlocks && topic.unlocks.length > 0)) && (
                <div className="flex items-center gap-1.5 flex-wrap animate-in fade-in duration-300">
                  {topic.reviewDue && (
                    <PlanChip
                      icon={RotateCw}
                      className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                      testId={`chip-review-due-${topic.topic}`}
                    >
                      Review due
                    </PlanChip>
                  )}
                  {topic.blockedBy && (
                    <PlanChip
                      icon={Lock}
                      className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                      testId={`chip-blocked-by-${topic.topic}`}
                    >
                      Start with {topic.blockedBy} first
                    </PlanChip>
                  )}
                  {topic.unlocks && topic.unlocks.length > 0 && (
                    <PlanChip
                      icon={Sparkles}
                      className="bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                      testId={`chip-unlocks-${topic.topic}`}
                    >
                      Unlocks {topic.unlocks[0]}
                      {topic.unlocks.length > 1 ? ` +${topic.unlocks.length - 1} more` : ""}
                    </PlanChip>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>Mastery</span>
                <span className={`font-medium ${getMasteryColor(topic.mastery)}`}>{topic.mastery}%</span>
              </div>
              <Progress value={topic.mastery} className="h-1.5" />
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                {topic.accuracy !== null && (
                  <span className="flex items-center gap-0.5">
                    <Target className="h-3 w-3" /> {topic.accuracy}% accuracy
                  </span>
                )}
                {topic.totalAttempted > 0 && (
                  <span className="flex items-center gap-0.5">
                    <BookOpen className="h-3 w-3" /> {topic.totalAttempted} answered
                  </span>
                )}
                {topic.daysSinceStudied > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-3 w-3" />
                    {topic.daysSinceStudied === 30 ? 'Never studied' :
                     topic.daysSinceStudied === 1 ? 'Yesterday' :
                     `${topic.daysSinceStudied}d ago`}
                  </span>
                )}
              </div>
              {topic.objectivesTotal > 0 && (
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-0.5">
                    <BarChart3 className="h-3 w-3" />
                    {topic.objectivesMastered}/{topic.objectivesTotal} objectives mastered
                  </span>
                  {topic.strugglingObjectiveCount > 0 && (
                    <span className="flex items-center gap-0.5 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      {topic.strugglingObjectiveCount} struggling
                    </span>
                  )}
                </div>
              )}
              {topic.reasons.length > 0 && !topic.completedToday && (
                <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30">
                  <Lightbulb className="h-3 w-3 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">
                    {topic.reasons.join(" · ")}
                  </p>
                </div>
              )}
              {topic.strugglingObjectives.length > 0 && !topic.completedToday && (
                <div className="space-y-0.5 mt-1">
                  {topic.strugglingObjectives.map((obj) => (
                    <p key={obj.code} className="text-[10px] text-muted-foreground pl-1 border-l-2 border-amber-300 dark:border-amber-700">
                      {obj.description} ({obj.accuracy}% accuracy)
                    </p>
                  ))}
                </div>
              )}
              {topic.completedToday && topic.todayAccuracy !== null && (
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Done today - {topic.todayAttempts} questions, {topic.todayAccuracy}% accuracy
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SubjectCard({ subject }: { subject: SubjectBreakdown }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md bg-muted/50">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${getSubjectIcon(subject.subject)}`}>
        <Brain className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm font-medium truncate">{subject.displayName}</p>
          <Badge variant="secondary">
            {getMasteryLabel(subject.avgMastery)}
          </Badge>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <Progress value={subject.avgMastery} className="h-1 flex-1" />
          <span className={`text-xs font-medium ${getMasteryColor(subject.avgMastery)}`}>{subject.avgMastery}%</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
          <span>{subject.topicCount} topics</span>
          {subject.avgAccuracy !== null && (
            <span>{subject.avgAccuracy}% accuracy</span>
          )}
          {subject.objectivesTotal > 0 && (
            <span>{subject.objectivesMastered}/{subject.objectivesTotal} objectives ({subject.objectiveMasteryRate}%)</span>
          )}
          {subject.weakTopicCount > 0 && (
            <span className="text-amber-600 dark:text-amber-400">{subject.weakTopicCount} need work</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function StudyPlan() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [showAllTopics, setShowAllTopics] = useState(false);

  useEffect(() => {
    if (user?.id) {
      try { localStorage.setItem(`quickstart_visited_study-plan_${user.id}`, "true"); } catch {}
    }
  }, [user?.id]);

  const { data: plan, isLoading, error } = useQuery<StudyPlanData>({
    queryKey: ['/api/study-plan'],
    enabled: isAuthenticated && !!user,
    staleTime: 60000,
  });

  if (authLoading || isLoading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 max-w-3xl mx-auto">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    setLocation("/login");
    return null;
  }

  if (error || !plan) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-6 max-w-3xl mx-auto">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Unable to load your study plan right now. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progressPercent = plan.totalPlanItems > 0
    ? Math.round((plan.completedCount / plan.totalPlanItems) * 100)
    : 0;

  const objSummary = plan.objectiveSummary;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Target className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" /> Smart Study Planner
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your personalized learning path for Grade {plan.grade}, powered by your progress report
        </p>
        {plan.ability && plan.ability.source === "irt" && (
          <p
            className="text-xs font-medium text-primary mt-1.5 flex items-center gap-1 animate-in fade-in duration-500"
            data-testid="text-ability-line"
          >
            <Globe2 className="h-3.5 w-3.5 flex-shrink-0" />
            Nationally you are ahead of {plan.ability.percentile}% of students
          </p>
        )}
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-sm font-medium">Today's Progress</p>
              <p className="text-xs text-muted-foreground">
                {plan.completedCount} of {plan.totalPlanItems} topics studied
              </p>
            </div>
            <div className={`text-2xl font-bold ${getMasteryColor(progressPercent)}`}>
              {progressPercent}%
            </div>
          </div>
          <Progress value={progressPercent} className="h-2" />
          {plan.completedCount === plan.totalPlanItems && plan.totalPlanItems > 0 && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
              <Zap className="h-3 w-3" /> Amazing! You've completed today's study plan!
            </p>
          )}
        </CardContent>
      </Card>

      {objSummary && objSummary.total > 0 && (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-primary flex-shrink-0" />
              <p className="text-sm font-medium">Objective Mastery</p>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Progress value={objSummary.masteryRate} className="h-2 flex-1" />
              <span className={`text-sm font-medium ${getMasteryColor(objSummary.masteryRate)}`}>{objSummary.masteryRate}%</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-950/30">
                <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{objSummary.mastered}</p>
                <p className="text-[11px] text-muted-foreground">Mastered</p>
              </div>
              <div className="p-2 rounded bg-amber-50 dark:bg-amber-950/30">
                <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{objSummary.progressing}</p>
                <p className="text-[11px] text-muted-foreground">In Progress</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-lg font-bold text-muted-foreground">{objSummary.notStarted}</p>
                <p className="text-[11px] text-muted-foreground">Not Started</p>
              </div>
            </div>
            <Button variant="outline" className="w-full mt-3" onClick={() => setLocation('/progress-report')}>
              View Full Progress Report <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {plan.focusSubject && (
        <Card className="border-amber-200 dark:border-amber-800/50">
          <CardContent className="p-3 sm:p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-amber-700 dark:text-amber-300" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium">Focus Area: {plan.focusSubject.name}</p>
              <p className="text-xs text-muted-foreground">
                {plan.focusSubject.mastery}% mastery
                {plan.focusSubject.accuracy !== null && ` · ${plan.focusSubject.accuracy}% accuracy`}
                {plan.focusSubject.weakTopics > 0 && ` · ${plan.focusSubject.weakTopics} topics need attention`}
              </p>
              {plan.focusSubject.objectivesTotal > 0 && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {plan.focusSubject.objectivesMastered}/{plan.focusSubject.objectivesTotal} objectives mastered
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {plan.topFocusAreas && plan.topFocusAreas.length > 0 && (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-sm font-medium">Objectives Needing Work</p>
            </div>
            <div className="space-y-1.5">
              {plan.topFocusAreas.map((area) => (
                <div key={area.code} className="flex items-start gap-2 p-2 rounded bg-muted/50">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium leading-tight">{area.description}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {area.topicName || area.subject} · {area.accuracy}% accuracy
                    </p>
                  </div>
                  <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex-shrink-0">
                    {area.accuracy}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
          <BookOpen className="h-4 w-4 flex-shrink-0" /> Today's Study Plan
        </h2>
        <div className="space-y-3">
          {plan.dailyPlan.map((topic, i) => (
            <TopicCard key={`${topic.subject}-${topic.topic}`} topic={topic} index={i} />
          ))}
        </div>
      </div>

      {plan.subjectBreakdown.length > 0 && (
        <div>
          <h2 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
            <Brain className="h-4 w-4 flex-shrink-0" /> Subject Overview
          </h2>
          <Card>
            <CardContent className="p-3 sm:p-4 space-y-2">
              {plan.subjectBreakdown.map((subj) => (
                <SubjectCard key={subj.subject} subject={subj} />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {plan.allTopics.length > plan.dailyPlan.length && (
        <div>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setShowAllTopics(!showAllTopics)}
          >
            {showAllTopics ? (
              <>Hide All Topics <ChevronUp className="h-4 w-4 ml-1" /></>
            ) : (
              <>View All {plan.totalTopics} Topics <ChevronDown className="h-4 w-4 ml-1" /></>
            )}
          </Button>
          {showAllTopics && (
            <div className="space-y-2 mt-3">
              {plan.allTopics.slice(5).map((topic, i) => (
                <TopicCard key={`${topic.subject}-${topic.topic}`} topic={topic} index={i + 5} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
